<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-truck me-2"></i>Supplier Purchase Orders
      </h4>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading orders...</p>
      </div>

      <!-- Orders Content -->
      <div v-if="!loading" class="orders-content fade-in">
        <!-- Orders Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ orders.total }}</span>
            <span class="text-muted">Orders</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search order" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Orders Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>SUPPLIER</th>
                  <th>STORE ORDER</th>
                  <th>BY USER</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(order, index) in orders.data" :key="order.id" class="order-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ order.id.split("-").pop() }}</td>
                  <td>{{ order.code }}</td>
                  <td>
                    <router-link v-if="order.supplier" :to="`/supplier/${order.supplier.id}`" class="text-decoration-none">
                      {{ order.supplier.code }} - {{ order.supplier.name }}
                    </router-link>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <router-link 
                      v-if="order.store_order" 
                      :to="`/store-order/${order.store_order.id}`"
                      class="text-primary text-decoration-none"
                    >
                      <i class="bi bi-box-seam me-1"></i>{{ order.store_order.code }}
                    </router-link>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>{{ order.user.first_name }} {{ order.user.last_name }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(order.status)">
                      {{ order.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(order.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary" @click="orderDetails(order.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import orderService from "@/api/orderService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import confirmDialog from "@/utils/confirmDialog";
import formatDate from "@/utils/formatDate";

export default {
  name: "Orders",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      profile: {},
      loading: true,
      dataTableHeaders: [
        '#',
        'Code',
        'Supplier',
        'By User',
        'Status',
        'Date',
        null
      ],
      orders: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      _searchDebounce: null,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatDate,
    confirmDialog,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
      }
    },
    async getOrders() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      const response = await orderService.getOrders(params)

      // Handle both array and paginated object responses
      if (Array.isArray(response)) {
        this.orders = {
          data: response,
          total: response.length,
          last_page: 1
        }
      } else {
        this.orders = response
      }

      this.loading = false
    },
    orderDetails(id) {
      router.push('/order/' + id)
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getOrders()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getOrders()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
  },

  computed: {
    totalPages(){
      return this.orders?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getOrders()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getOrders()
    },
  },

  created: async function () {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getOrders()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
/* Orders page styles */
.orders-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.order-row {
  transition: all 0.2s ease;
}

.order-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
